/**
 * Rebajas future price (collection cards)
 *
 * During the announce window before a discount season ("rebajas") starts,
 * the back-end exposes a `future_price` for each variant. This script fetches
 * those prices for the products visible in the collection grid and renders the
 * upcoming-discount info inside each card's `.brn-card__footer`.
 *
 * Data source: window.rebajas (set in head-init.liquid) + the hydrogen
 * `future_prices` endpoint. See app/utils/helpers/rebajas.jsx for the shape.
 */
(function () {
  const rebajas = window.rebajas;

  // No pricelist configured for this market → nothing to announce.
  if (!rebajas || !rebajas.pricelist || !rebajas.countryIsoCode) return;

  const endpoint = window.config && window.config.hydrogenEndpoint;
  if (!endpoint) return;

  const RENDERED_ATTR = 'data-rebajas-future';
  const BATCH_SIZE = 50;

  /**
   * Collect not-yet-processed cards keyed by their (clean, numeric) variant id.
   * @param {ParentNode} root
   * @returns {Map<string, HTMLElement>}
   */
  function collectCards(root) {
    const map = new Map();
    const cards = root.querySelectorAll('.collection__grid__item[data-variant-id]');
    cards.forEach((card) => {
      if (card.hasAttribute(RENDERED_ATTR)) return;
      const id = (card.getAttribute('data-variant-id') || '').trim();
      if (!/^\d+$/.test(id)) return;
      // Mark eagerly so concurrent passes (e.g. infinite scroll) don't re-request.
      card.setAttribute(RENDERED_ATTR, 'pending');
      // Keep the last card for a given variant id; collisions are unlikely.
      map.set(id, card);
    });
    return map;
  }

  /** Format a major-unit amount (e.g. 25.9) using the market money format. */
  function formatMoney(amount) {
    if (amount == null || isNaN(amount)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(Math.round(amount * 100));
    }
    return amount.toFixed(2).replace('.', ',');
  }

  // Renders: "25,90 €  -10%  24/06 - 19H App"
  function buildFuturePriceNode(futurePrice) {
    const wrapper = document.createElement('div');
    wrapper.className = 'brn-card__future-price';

    if (futurePrice.price != null) {
      const price = document.createElement('span');
      price.className = 'brn-card__future-price__price';
      console.log(futurePrice.price)
      price.textContent = formatMoney(futurePrice.price).replace(/\s/g, '');
      wrapper.appendChild(price);
    }

    if (futurePrice.percentage) {
      const percent = document.createElement('span');
      percent.className = 'brn-card__future-price__percent';
      percent.textContent = '-' + futurePrice.percentage + '%';
      wrapper.appendChild(percent);
    }

    if (futurePrice.visibility) {
      const message = document.createElement('span');
      message.className = 'brn-card__future-price__message';
      message.textContent = futurePrice.visibility + ' ' + window.rebajas.activationMessage;
      wrapper.appendChild(message);
    }

    return wrapper;
  }

  function renderCard(card, futurePrice) {
    const footer = card.querySelector('.brn-card__footer');
    if (!footer) return;
    // Avoid duplicates if this card somehow gets processed twice.
    const existing = footer.querySelector('.brn-card__future-price');
    if (existing) existing.remove();
    footer.appendChild(buildFuturePriceNode(futurePrice));
  }

  async function fetchFuturePrices(variantIds) {
    const url =
      endpoint.replace(/\/$/, '') +
      '/rest/1/products/future_prices/' +
      encodeURIComponent(rebajas.countryIsoCode) +
      '/' +
      variantIds.join(',');

    console.log('URL', url)
    const headers = { 'Content-Type': 'application/json' };
    if (window.config && window.config.hydrogenBearer) {
      headers.Authorization = 'Bearer ' + window.config.hydrogenBearer;
    }

    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error('future_prices request failed: ' + response.status);
    }
    const data = await response.json();
    return (data && data.prices) || {};
  }

  /** Process a set of cards: fetch prices in batches and render results. */
  async function processCards(cardsById) {
    const ids = Array.from(cardsById.keys());
    if (ids.length === 0) return;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      let prices;
      try {
        prices = await fetchFuturePrices(batch);
        console.log('PRICES', prices)
      } catch (error) {
        console.warn('[rebajas] future price fetch error:', error);
        // Release the cards so a later trigger can retry.
        batch.forEach((id) => {
          const card = cardsById.get(id);
          if (card) card.removeAttribute(RENDERED_ATTR);
        });
        continue;
      }

      batch.forEach((id) => {
        const card = cardsById.get(id);
        if (!card) return;
        const entry = prices[id];
        if (entry && entry.future_price) {
          renderCard(card, entry.future_price);
          card.setAttribute(RENDERED_ATTR, 'done');
        } else {
          // No future price for this variant; leave the card untouched.
          card.setAttribute(RENDERED_ATTR, 'none');
        }
      });
    }
  }

  function run(root) {
    const cardsById = collectCards(root || document);
    if (cardsById.size > 0) processCards(cardsById);
  }

  // --- Single-price contexts (product page + cart drawer) -------------------
  // Unlike collection cards, the product page and each cart line render a
  // `.price__container[data-variant-id]` (see snippets/price.liquid). We fetch
  // the future price for those variants and drop the same message node either
  // inside the container (product page) or right after it (cart drawer).

  // Product page: the main PDP price block.
  const PRODUCT_PRICE_SELECTOR = '.brn-product__price .price__container[data-variant-id]';
  // Cart drawer: every line item's price container.
  const CART_PRICE_SELECTOR = 'cart-drawer-items .price__container[data-variant-id]';

  /**
   * Insert a future-price node relative to `container`.
   * @param {'inside'|'after'} position
   */
  function placeFuturePrice(container, futurePrice, position) {
    const node = buildFuturePriceNode(futurePrice);
    if (position === 'after') {
      // Avoid duplicates if the container is processed twice.
      const sibling = container.nextElementSibling;
      if (sibling && sibling.classList.contains('brn-card__future-price')) sibling.remove();
      container.insertAdjacentElement('afterend', node);
    } else {
      const existing = container.querySelector('.brn-card__future-price');
      if (existing) existing.remove();
      container.appendChild(node);
    }
  }

  /** Fetch prices for a set of single-price containers and render the result. */
  async function processContainers(containers, position) {
    const byId = new Map();
    containers.forEach((container) => {
      if (container.hasAttribute(RENDERED_ATTR)) return;
      const id = (container.getAttribute('data-variant-id') || '').trim();
      if (!/^\d+$/.test(id)) return;
      // Mark eagerly so concurrent passes don't re-request.
      container.setAttribute(RENDERED_ATTR, 'pending');
      byId.set(id, container);
    });

    const ids = Array.from(byId.keys());
    if (ids.length === 0) return;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      let prices;
      try {
        prices = await fetchFuturePrices(batch);
      } catch (error) {
        console.warn('[rebajas] future price fetch error:', error);
        // Release the containers so a later trigger can retry.
        batch.forEach((id) => {
          const container = byId.get(id);
          if (container) container.removeAttribute(RENDERED_ATTR);
        });
        continue;
      }

      batch.forEach((id) => {
        const container = byId.get(id);
        if (!container) return;
        const entry = prices[id];
        if (entry && entry.future_price) {
          placeFuturePrice(container, entry.future_price, position);
          container.setAttribute(RENDERED_ATTR, 'done');
        } else {
          container.setAttribute(RENDERED_ATTR, 'none');
        }
      });
    }
  }

  function runProductPrice(root) {
    const containers = (root || document).querySelectorAll(PRODUCT_PRICE_SELECTOR);
    if (containers.length > 0) processContainers(containers, 'after');
  }

  function runCartPrices(root) {
    const containers = (root || document).querySelectorAll(CART_PRICE_SELECTOR);
    if (containers.length > 0) processContainers(containers, 'after');
  }

  function init(root) {
    run(root);
    runProductPrice(root);
    runCartPrices(root);
  }

  // Initial pass over the server-rendered grid, product page and cart drawer.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Infinite scroll appends new cards; process them as they appear.
  window.addEventListener('infiniteScroll:loaded', function () {
    run(document);
  });

  // The PDP replaces the price block's markup on variant change, wiping our
  // node, so re-render once the new variant's price is in the DOM.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      runProductPrice(document);
    });
  }

  // The cart drawer re-renders its line items on every update.
  document.addEventListener('cart-drawer:refreshed', function () {
    runCartPrices(document);
  });
})();
